import { composeEventHandlers } from '@radix-ui/primitive';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import { Key } from 'w3c-keys';
import getLocaleData from '../../locale/getLocaleData.mjs';
import { localeValues } from '../components/localeValues.mjs';
import { DATE_FORMAT_RECORD, FORMAT_REGEX_PATTERNS } from '../constants/date-format.mjs';
import { isInvalidDate, onDateChangeWhenChanged } from '../utils/date.mjs';
import { normalizeDateInput } from '../utils/normalize.mjs';

function useDatePickerField(params) {
  const {
    date,
    dateFormatRecord = DATE_FORMAT_RECORD,
    locale: localeFromProps,
    onDateChange,
    onBlur,
    onChange,
    onKeyDown,
    onValidate,
    inputType
  } = params;
  const dateFormat = dateFormatRecord[localeFromProps];
  const locale = getLocaleData(localeFromProps);
  const { invalidDateInputErrorMessage } = localeValues[localeFromProps];
  const [dateString, setDateString] = useState(
    () => date !== null && !isInvalidDate(date) ? format(date, dateFormat, { locale }) : ""
  );
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const clearError = () => {
    setError(false);
    setErrorMessage(void 0);
  };
  const handleChange = composeEventHandlers(onChange, (event) => {
    if (!event.isDefaultPrevented()) {
      const { value } = event.target;
      setDateString(value);
    }
  });
  const handleApplyCurrentInputValue = () => {
    const isEmptyDateString = dateString.trim().length === 0;
    if (isEmptyDateString) {
      clearError();
      onDateChangeWhenChanged({ date, changedDate: null }, onDateChange);
      return;
    }
    const normalizedDateString = normalizeDateInput({
      value: dateString,
      patterns: FORMAT_REGEX_PATTERNS,
      referenceDate: /* @__PURE__ */ new Date(),
      inputType
    });
    if (normalizedDateString === null) {
      setError(true);
      setErrorMessage(invalidDateInputErrorMessage);
      return;
    }
    const [valid, validationMessage] = typeof onValidate === "function" ? onValidate(normalizedDateString) : [true, void 0];
    switch (true) {
      case !valid: {
        setError(true);
        setErrorMessage(validationMessage ?? invalidDateInputErrorMessage);
        break;
      }
      case isInvalidDate(normalizedDateString): {
        setError(true);
        setErrorMessage(invalidDateInputErrorMessage);
        break;
      }
      default: {
        clearError();
        onDateChangeWhenChanged(
          { date, changedDate: normalizedDateString },
          onDateChange
        );
      }
    }
  };
  const handleBlur = composeEventHandlers(onBlur, (event) => {
    if (!event.isDefaultPrevented()) {
      handleApplyCurrentInputValue();
    }
  });
  const handleKeyDown = composeEventHandlers(onKeyDown, (event) => {
    if (!event.isDefaultPrevented()) {
      const isEnterKeyDown = event.key === Key.Enter;
      if (isEnterKeyDown) {
        handleApplyCurrentInputValue();
      }
    }
  });
  useEffect(() => {
    if (date === null || isInvalidDate(date)) {
      setDateString("");
      return;
    }
    clearError();
    setDateString(format(date, dateFormat, { locale }));
  }, [date, dateFormat, locale]);
  return [
    dateString,
    { onBlur: handleBlur, onChange: handleChange, onKeyDown: handleKeyDown },
    { error, errorMessage },
    handleApplyCurrentInputValue
  ];
}

export { useDatePickerField };
//# sourceMappingURL=useDatePickerField.mjs.map
